// ===== WiFi Lab Page =====
import React, { useState } from 'react';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { WifiIcon, ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const WiFiLabPage = () => {
  const { canAccess } = useVenueDashboard();
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);

  const runSpeedTest = () => {
    setTesting(true);
    setResults(null);
    
    // Simulate speed test
    setTimeout(() => {
      setResults({
        download: (Math.random() * 100 + 20).toFixed(1),
        upload: (Math.random() * 50 + 10).toFixed(1),
        ping: Math.floor(Math.random() * 50 + 10),
        timestamp: new Date().toLocaleTimeString()
      });
      setTesting(false);
    }, 3000);
  };

  if (!canAccess(['STAFF', 'MANAGER', 'OWNER', 'ADMIN'])) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <VenueDashboardHeader title="WiFi Lab" />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ color: '#ef4444' }}>Access denied. Staff role or higher required.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Wi-Fi Lab" />
      
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        {/* Speed Test Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px 24px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: testing ? '#dbeafe' : '#ecfdf5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            {testing ? (
              <ArrowPathIcon style={{ 
                width: '40px', 
                height: '40px', 
                color: '#3b82f6',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <WifiIcon style={{ width: '40px', height: '40px', color: '#10b981' }} />
            )}
          </div>
          
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0' }}>
            {testing ? 'Testing...' : 'Wi-Fi Speed Test'}
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            {testing ? 'Please wait while we measure your connection' : 'Test your venue\'s Wi-Fi speed'}
          </p>
          
          <button
            onClick={runSpeedTest}
            disabled={testing}
            style={{
              padding: '14px 32px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: testing ? '#9ca3af' : '#10b981',
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              cursor: testing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
            data-testid="run-speed-test-button"
          >
            {testing ? (
              <>
                <ArrowPathIcon style={{ width: '20px', height: '20px' }} />
                Testing...
              </>
            ) : (
              <>
                <WifiIcon style={{ width: '20px', height: '20px' }} />
                Run Speed Test
              </>
            )}
          </button>
        </div>

        {/* Results */}
        {results && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <CheckCircleIcon style={{ width: '24px', height: '24px', color: '#10b981' }} />
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                Test Complete
              </h3>
              <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: 'auto' }}>
                {results.timestamp}
              </span>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>Download</p>
                <p style={{ fontSize: '28px', fontWeight: '700', color: '#10b981', margin: 0 }}>
                  {results.download}
                </p>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>Mbps</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>Upload</p>
                <p style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6', margin: 0 }}>
                  {results.upload}
                </p>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>Mbps</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>Ping</p>
                <p style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b', margin: 0 }}>
                  {results.ping}
                </p>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>ms</p>
              </div>
            </div>
            
            <button
              onClick={runSpeedTest}
              style={{
                width: '100%',
                marginTop: '20px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                color: '#374151',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Run Again
            </button>
          </div>
        )}

        {/* Tips */}
        <div style={{
          marginTop: '24px',
          backgroundColor: '#fffbeb',
          borderRadius: '12px',
          padding: '16px',
          borderLeft: '4px solid #f59e0b'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', margin: '0 0 8px 0' }}>
            📡 Wi-Fi Tips
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#78350f', fontSize: '13px' }}>
            <li>Test from different areas of your venue</li>
            <li>Run tests during peak and off-peak hours</li>
            <li>Minimum 10 Mbps recommended for good experience</li>
          </ul>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default WiFiLabPage;
